import { useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, Filter, Loader2, X } from 'lucide-react';
import { adsApi } from '@/api/client';
import { cn } from '@/utils/cn';
import { Button } from '@/components/ui/Button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover';

function useDebouncedValue(value: string, delayMs: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

function searchTermFilterLabel(selectedTerms: string[]): string {
  if (selectedTerms.length === 0) return 'All search terms';
  if (selectedTerms.length === 1) return selectedTerms[0] || '1 search term';
  return `${selectedTerms.length} search terms selected`;
}

function toggleTerm(selectedTerms: string[], term: string, checked: boolean): string[] {
  if (checked) {
    if (selectedTerms.includes(term)) return selectedTerms;
    return [...selectedTerms, term];
  }
  return selectedTerms.filter((item) => item !== term);
}

export function DaypartingSearchTermFilter({
  profileId,
  campaignIds,
  keywordIds,
  selectedSearchTerms,
  onChange,
}: {
  profileId: string;
  campaignIds: string[];
  keywordIds: string[];
  selectedSearchTerms: string[];
  onChange: (searchTerms: string[]) => void;
}) {
  const labelId = useId();
  const searchInputId = useId();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [draftTerms, setDraftTerms] = useState<string[]>([]);
  const debouncedSearch = useDebouncedValue(search, 250);
  const campaignKey = useMemo(() => [...campaignIds].sort().join(','), [campaignIds]);
  const keywordKey = useMemo(() => [...keywordIds].sort().join(','), [keywordIds]);

  const termsQuery = useQuery({
    queryKey: ['dayparting-v2-search-terms', profileId, debouncedSearch, campaignKey, keywordKey],
    queryFn: async () => {
      const res = await adsApi.getStreamV2SearchTerms(profileId, {
        q: debouncedSearch || undefined,
        campaignIds: campaignIds.length > 0 ? campaignIds : undefined,
        keywordIds: keywordIds.length > 0 ? keywordIds : undefined,
        limit: 50,
      });
      if (!res.success) throw new Error('Failed to load search terms');
      return res.data?.searchTerms ?? [];
    },
    enabled: open && Boolean(profileId),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const visibleTerms = useMemo(() => {
    const fromApi = termsQuery.data ?? [];
    const seen = new Set(fromApi);
    const extras = draftTerms.filter((term) => !seen.has(term));
    return [...extras, ...fromApi];
  }, [draftTerms, termsQuery.data]);

  const activeTerms = open ? draftTerms : selectedSearchTerms;
  const selectedSet = useMemo(() => new Set(activeTerms), [activeTerms]);
  const triggerLabel = searchTermFilterLabel(selectedSearchTerms);
  const loading = termsQuery.isPending || termsQuery.isFetching;
  const isFiltered = selectedSearchTerms.length > 0;

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (nextOpen) {
      setDraftTerms(selectedSearchTerms);
      return;
    }
    setDraftTerms(selectedSearchTerms);
    setSearch('');
  };

  const selectVisible = () => {
    const next = new Set(draftTerms);
    const merged = [...draftTerms];
    for (const term of visibleTerms) {
      if (next.has(term)) continue;
      next.add(term);
      merged.push(term);
    }
    setDraftTerms(merged);
  };

  const applyDraft = () => {
    onChange(draftTerms);
    setOpen(false);
    setSearch('');
  };

  const panel: ReactNode = (
    <>
      <div className="space-y-3 border-b border-border px-3 py-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-sm font-semibold text-foreground">Select search terms</p>
            <p className="text-xs text-foreground-subtle">
              Search customer queries. Check one or more exact terms.
            </p>
          </div>
          {draftTerms.length > 0 ? (
            <button
              type="button"
              className="shrink-0 text-xs font-medium text-foreground-muted hover:text-foreground"
              onClick={() => setDraftTerms([])}
            >
              Clear all
            </button>
          ) : null}
        </div>
        <div>
          <label htmlFor={searchInputId} className="sr-only">
            Search terms
          </label>
          <input
            id={searchInputId}
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Type to find search terms"
            className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
            autoFocus
          />
        </div>
        {visibleTerms.length > 0 ? (
          <button
            type="button"
            className="text-xs font-medium text-brand-deep hover:underline"
            onClick={selectVisible}
          >
            Select all matching ({visibleTerms.length})
          </button>
        ) : null}
      </div>
      <div className="max-h-64 overflow-y-auto px-1 py-1" role="listbox" aria-multiselectable="true">
        {termsQuery.isError ? (
          <p className="px-3 py-4 text-sm text-error">Failed to load search terms.</p>
        ) : loading && visibleTerms.length === 0 ? (
          <p className="px-3 py-4 text-sm text-foreground-muted">Loading search terms…</p>
        ) : visibleTerms.length === 0 ? (
          <p className="px-3 py-4 text-sm text-foreground-muted">No search terms match.</p>
        ) : (
          <ul className="m-0 list-none p-0">
            {visibleTerms.map((term) => {
              const checked = selectedSet.has(term);
              return (
                <li key={term} role="option" aria-selected={checked}>
                  <label
                    className={cn(
                      'flex cursor-pointer items-start gap-3 rounded-lg px-2.5 py-2.5 text-sm',
                      checked ? 'bg-brand-subtle/50' : 'hover:bg-surface-muted'
                    )}
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 rounded border-border text-brand focus:ring-brand/30"
                      checked={checked}
                      onChange={(event) =>
                        setDraftTerms(toggleTerm(draftTerms, term, event.target.checked))
                      }
                    />
                    <span className="min-w-0 truncate font-medium text-foreground">{term}</span>
                  </label>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <div className="flex items-center justify-between gap-2 border-t border-border px-3 py-2.5">
        <p className="text-xs text-foreground-subtle">
          {draftTerms.length > 0
            ? `${draftTerms.length} search term${draftTerms.length === 1 ? '' : 's'} selected`
            : 'All search terms included'}
        </p>
        <Button variant="secondary" size="sm" onClick={applyDraft}>
          Done
        </Button>
      </div>
    </>
  );

  return (
    <div
      className="rounded-xl border border-border bg-surface p-3 shadow-sm sm:p-4"
      role="group"
      aria-labelledby={labelId}
    >
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 space-y-1">
          <p id={labelId} className="text-sm font-semibold text-foreground">
            Search term filter
          </p>
          <p className="text-xs text-foreground-subtle">
            Limit the heatmap and summary cards to customer search terms. Leave unselected to
            include every term.
          </p>
        </div>
        <Popover open={open} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <button
              type="button"
              disabled={!profileId}
              aria-labelledby={labelId}
              aria-haspopup="listbox"
              aria-expanded={open}
              className={cn(
                'inline-flex w-full min-w-[14rem] max-w-full items-center justify-between gap-2 rounded-lg border px-3 py-2.5 text-sm lg:w-auto lg:min-w-[18rem]',
                'transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                isFiltered
                  ? 'border-brand/40 bg-brand-subtle/40 text-foreground hover:bg-brand-subtle/60'
                  : 'border-border bg-page text-foreground hover:bg-page/80'
              )}
            >
              <span className="inline-flex min-w-0 items-center gap-2">
                <Filter
                  className={cn(
                    'h-4 w-4 shrink-0',
                    isFiltered ? 'text-brand-deep' : 'text-foreground-muted'
                  )}
                  aria-hidden
                />
                <span className="truncate text-left">{triggerLabel}</span>
              </span>
              {loading && open ? (
                <Loader2 className="h-4 w-4 shrink-0 animate-spin text-brand" aria-hidden />
              ) : (
                <ChevronDown className="h-4 w-4 shrink-0 text-foreground-muted" aria-hidden />
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-[min(26rem,calc(100vw-2rem))] p-0">
            {panel}
          </PopoverContent>
        </Popover>
      </div>

      {isFiltered ? (
        <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t border-border/70 pt-3">
          <span className="text-[11px] font-medium uppercase tracking-wide text-foreground-subtle">
            Active
          </span>
          {selectedSearchTerms.map((term) => (
            <span
              key={term}
              className="inline-flex max-w-full items-center gap-1 rounded-full border border-brand/30 bg-brand-subtle/60 px-2.5 py-1 text-xs text-foreground"
            >
              <span className="truncate">{term}</span>
              <button
                type="button"
                className="shrink-0 rounded-full text-foreground-muted hover:text-foreground"
                aria-label={`Remove ${term}`}
                onClick={() => onChange(toggleTerm(selectedSearchTerms, term, false))}
              >
                <X className="h-3 w-3" aria-hidden />
              </button>
            </span>
          ))}
          <button
            type="button"
            className="text-xs font-medium text-foreground-muted hover:text-foreground"
            onClick={() => onChange([])}
          >
            Clear all
          </button>
        </div>
      ) : null}
    </div>
  );
}
